
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/use-debounce";
import { fetchAdminUsersList } from "@/services/adminUserService";
import { AdminUser } from "@/types/admin";

export const useAdminUsers = () => {
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;
  const { toast } = useToast();
  
  const debouncedSearch = useDebounce(searchQuery, 500);

  const fetchAdminUsers = async () => {
    try {
      setLoading(true);
      
      const { data, count } = await fetchAdminUsersList(page, pageSize, debouncedSearch);
      
      setAdminUsers(data || []);
      setTotalCount(count || 0);
    } catch (error: any) {
      console.error('Error fetching admin users:', error);
      toast({
        title: "Error",
        description: "Failed to fetch admin users",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(1); // Reset to first page when search changes
  };

  useEffect(() => {
    fetchAdminUsers();
  }, [debouncedSearch, page]);

  return {
    adminUsers,
    loading,
    searchQuery,
    handleSearchChange,
    page,
    pageSize,
    totalCount,
    handlePageChange,
  };
};
